/**
 * Versionado histórico de campos del empleado por fecha efectiva.
 *
 * Diseño:
 *   - Cada empleado tiene un array `__cambios` embedded en su documento.
 *   - Cada entrada: { id, field, old_value, new_value, effective_from (YYYY-MM-DD), created_at }
 *   - Para resolver el valor de un campo versionado en una fecha dada:
 *       · Si fecha >= cambio.effective_from del más reciente aplicable → cambio.new_value
 *       · Si hay cambios y fecha < effective_from del cambio más viejo → ese cambio.old_value
 *       · Si no hay cambios → empleado[field] actual
 *
 * Campos versionados = los que impactan cálculos de planilla/asistencia:
 */
export const VERSIONED_FIELDS = [
  "almuerzo_paga",
  "salario_base",
  "puesto",
  "ccss_aplica",
  "pensionado",
  "jornada",
  "frecuencia_pago",
  "jornada_especial_48",
  "salario_cuentas",
  "renta_conyuge",
  "renta_hijos",
  "renta_modo",
];

const BOOL_FIELDS = ["almuerzo_paga", "ccss_aplica", "pensionado", "jornada_especial_48", "renta_conyuge"];
const DEFAULTS = { renta_hijos: 0, renta_modo: "quincena" };

export const FIELD_LABELS = {
  almuerzo_paga: "Hora de almuerzo paga",
  salario_base: "Salario base",
  puesto: "Puesto",
  ccss_aplica: "CCSS aplica",
  pensionado: "Pensionado",
  jornada: "Jornada",
  frecuencia_pago: "Frecuencia de pago",
  jornada_especial_48: "Jornada especial (extras > 48 h semanales)",
  salario_cuentas: "Salario dividido en dos cuentas",
  renta_conyuge: "Renta: cónyuge sin ingresos",
  renta_hijos: "Renta: cantidad de hijos",
  renta_modo: "Renta: cuándo se rebaja",
};

/** Formatea el valor de un campo para mostrarlo en el mini-historial */
export function formatFieldValue(field, value) {
  if (value == null || value === "") return "—";
  if (field === "almuerzo_paga") return value ? "Pagado" : "No pagado";
  if (field === "ccss_aplica") return value ? "Sí" : "No";
  if (field === "pensionado") return value ? "Sí" : "No";
  if (field === "jornada_especial_48") return value ? "Activa" : "Inactiva";
  if (field === "renta_conyuge") return value ? "Sí" : "No";
  if (field === "renta_modo") return value === "segunda" ? "Solo en la II quincena" : "En cada quincena";
  if (field === "salario_cuentas") {
    if (!value.activo) return "No dividido";
    const f = (n) => `₡${Number(n || 0).toLocaleString("es-CR")}`;
    return `${value.c1_nombre || "Cuenta 1"} ${f(value.c1_monto)} + ${value.c2_nombre || "Cuenta 2"} ${f(value.c2_monto)}`;
  }
  if (field === "salario_base") return `₡${Number(value).toLocaleString("es-CR")}`;
  return String(value);
}

/** Devuelve los cambios del empleado para un campo, ordenados ASC por effective_from. */
export function cambiosDeCampo(emp, field) {
  const all = Array.isArray(emp?.__cambios) ? emp.__cambios : [];
  return all
    .filter(c => c && c.field === field && c.effective_from)
    .slice()
    .sort((a, b) => String(a.effective_from).localeCompare(String(b.effective_from)));
}

/** Resuelve el valor de un campo versionado para una fecha dada (YYYY-MM-DD). */
export function getEmpValueAt(emp, field, fecha) {
  if (!emp) return undefined;
  if (!VERSIONED_FIELDS.includes(field)) return emp[field];
  const changes = cambiosDeCampo(emp, field);
  if (!changes.length) return emp[field];
  const f = String(fecha || "").slice(0, 10);
  if (!f) return emp[field];
  // Si fecha < primer cambio → old_value del primer cambio
  if (f < changes[0].effective_from) return changes[0].old_value;
  // Encontrar el cambio más reciente cuya effective_from <= fecha
  let aplicable = changes[0];
  for (const c of changes) {
    if (c.effective_from <= f) aplicable = c;
    else break;
  }
  return aplicable.new_value;
}

/** Devuelve una copia superficial del empleado con todos los campos versionados
 *  resueltos al valor efectivo en la fecha dada.
 *  Útil para pasar al cálculo de planilla/horas de una fecha histórica.
 */
export function getEmpAt(emp, fecha) {
  if (!emp) return emp;
  const out = { ...emp };
  for (const field of VERSIONED_FIELDS) {
    out[field] = getEmpValueAt(emp, field, fecha);
  }
  return out;
}

/** Genera un ID para una nueva entrada de cambio. */
export function nuevoCambioId() {
  return `camb_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
}

/** Dados dos objetos empleado (previo y nuevo), retorna la lista de campos versionados
 *  que cambiaron, con `old_value` y `new_value`. Útil para detectar qué pedirle al usuario.
 */
export function diffVersionedFields(prev, next) {
  const diffs = [];
  for (const field of VERSIONED_FIELDS) {
    const pv = prev ? prev[field] : undefined;
    const nv = next ? next[field] : undefined;
    // Normalizar booleans a true/false (checkbox puede dar undefined vs false)
    const normA = BOOL_FIELDS.includes(field) ? !!pv : (pv ?? DEFAULTS[field] ?? "");
    const normB = BOOL_FIELDS.includes(field) ? !!nv : (nv ?? DEFAULTS[field] ?? "");
    if (field === "salario_cuentas") {
      const norm = (v) => (v?.activo ? JSON.stringify([v.c1_nombre || "", Number(v.c1_monto) || 0, v.c2_nombre || "", Number(v.c2_monto) || 0]) : "off");
      if (norm(pv) !== norm(nv)) diffs.push({ field, old_value: pv ?? null, new_value: nv ?? null });
      continue;
    }
    if (normA !== normB) diffs.push({ field, old_value: pv ?? null, new_value: nv ?? null });
  }
  return diffs;
}
